import { useState, useEffect } from "react";
import { Modal, Upload, Button, Typography, Divider, Spin } from "antd";
import { InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { deleteApi, getApi, postApi } from "redux/apis";
import { toastText } from "utils/utils";
import dayjs from "dayjs";
import ConfirmDelete from "components/Global/confirmDeleteModel";

const { Dragger } = Upload;
const { Text } = Typography;

interface UploadMetadata {
  fileName: string;
  uploadedAt: string;
  rowCount: number;
  user: { firstName: string | null; lastName: string | null; email: string | null };
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const IncomingInventoryUploadModal = ({ isOpen, onClose, onSuccess }: Props) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [metadata, setMetadata] = useState<UploadMetadata | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchMetadata();
    }
  }, [isOpen]);

  const fetchMetadata = async () => {
    setMetadataLoading(true);
    try {
      const response = await getApi("/incoming-inventory/metadata");
      setMetadata(response?.data?.data || null);
    } catch {
      setMetadata(null);
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeleting(true);
    try {
      await deleteApi("/incoming-inventory/delete-all");
      toastText("All incoming inventory data deleted successfully", "success");
      setMetadata(null);
      setDeleteConfirmOpen(false);
      onSuccess();
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Failed to delete incoming inventory data",
        "error"
      );
    } finally {
      setDeleting(false);
    }
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      toastText("Please select a file", "error");
      return;
    }

    const file = (fileList[0].originFileObj || fileList[0]) as File;
    if (!file) {
      toastText("File object is missing", "error");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await postApi("/incoming-inventory/upload", formData, true);
      const data = response?.data?.data;

      toastText(
        `Upload successful`,
        "success"
      );

      setFileList([]);
      onSuccess();
      onClose();
    } catch (error: any) {
      toastText(
        error?.response?.data?.message || "Failed to upload incoming inventory file",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    setFileList([]);
    setDeleteConfirmOpen(false);
    onClose();
  };

  return (
    <>
    <Modal
      title="Upload Incoming Inventory"
      open={isOpen}
      onCancel={handleCancel}
      centered
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          loading={uploading}
          disabled={fileList.length === 0}
        >
          Upload
        </Button>,
      ]}
      destroyOnClose
      maskClosable={false}
    >
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Upload an Excel file (.xlsx, .xls) containing incoming shipments. Required
        columns: <strong>SKU #</strong>, <strong>QTY</strong>, <strong>EDA to FTZ</strong>.
      </Text>

      <Dragger
        accept=".xlsx,.xls"
        maxCount={1}
        fileList={fileList}
        beforeUpload={(file) => {
          const isExcel =
            file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            file.type === "application/vnd.ms-excel";
          const ext = file.name.split(".").pop()?.toLowerCase();

          if (!isExcel && ext !== "xlsx" && ext !== "xls") {
            toastText("Only Excel files (.xlsx, .xls) are allowed", "error");
            return Upload.LIST_IGNORE;
          }

          if (file.size > 5 * 1024 * 1024) {
            toastText("File size must be less than 5MB", "error");
            return Upload.LIST_IGNORE;
          }

          setFileList([file as any]);
          return false;
        }}
        onRemove={() => {
          setFileList([]);
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
        <p className="ant-upload-hint">
          Single .xlsx or .xls file, max 5MB
        </p>
      </Dragger>

      {metadataLoading ? (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <Spin size="small" />
        </div>
      ) : metadata ? (
        <>
          <Divider style={{ margin: "16px 0" }} />
          <div
            style={{
              background: "#f6f8fa",
              borderRadius: 6,
              padding: "12px 16px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div>
              <Text strong style={{ display: "block", marginBottom: 4 }}>
                Previously Uploaded File
              </Text>
              <Text style={{ display: "block" }}>
                <strong>File:</strong> {metadata.fileName}
              </Text>
              <Text style={{ display: "block" }}>
                <strong>Uploaded at:</strong>{" "}
                {dayjs(metadata.uploadedAt).format("MM/DD/YYYY hh:mm A")}
              </Text>
            </div>
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </Button>
          </div>
        </>
      ) : null}

    </Modal>

    {deleteConfirmOpen && (
      <ConfirmDelete
        isModalOpen={deleteConfirmOpen}
        handleOk={() => setDeleteConfirmOpen(false)}
        handleCancel={() => setDeleteConfirmOpen(false)}
        deleteHandler={handleDeleteAll}
        isAddUserLoading={deleting}
        warningMessage="This will delete all incoming inventory data. This action is irreversible. Are you sure?"
      />
    )}
    </>
  );
};

export default IncomingInventoryUploadModal;
